import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import AutoComplete from '../components/AutoComplete';
import { articuloService, tablaService, unidadMedidaService } from '../services/api';
import '../styles/datatable.css';
import '../styles/articulos.css';
import { getErrorDetail } from '../utils/errorHelper';

const Articulos = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [autoCode, setAutoCode] = useState(true);

  const [unidades, setUnidades] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tiposExistencia, setTiposExistencia] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);

  const [miniModal, setMiniModal] = useState(null);
  const [miniForm, setMiniForm] = useState({ codigo: '', descripcion: '' });

  const emptyForm = {
    codigo_interno: '', codigo_sunat: '', descripcion: '', descripcion_alt: '',
    id_unidad: '', peso: '', id_marca: '', id_familia: '', id_grupo: '',
    id_linea: '', id_categoria: '', pais: '', id_cuenta_contable: '', id_tipo_existencia: '', id_modelo: '',
    ancho: '', largo: '', grosor: '', volumen: '', area: '', medida: '',
    stock_minimo: '', stock_maximo: '', porcentaje_igv_compra: 18,
  };
  const [form, setForm] = useState(emptyForm);

  const fetchAll = useCallback(async () => {
    try {
      const [art, uni, mar, fam, gru, lin, cat, te, cc, mod] = await Promise.all([
        articuloService.getAll(),
        unidadMedidaService.getAll(),
        tablaService('marcas').getAll(),
        tablaService('familias').getAll(),
        tablaService('grupos').getAll(),
        tablaService('lineas').getAll(),
        tablaService('categorias').getAll(),
        tablaService('tipos-existencia').getAll(),
        tablaService('cuentas-contables').getAll(),
        tablaService('modelos').getAll(),
      ]);
      setRecords(art.data);
      setUnidades(uni.data);
      setMarcas(mar.data);
      setFamilias(fam.data);
      setGrupos(gru.data);
      setLineas(lin.data);
      setCategorias(cat.data);
      setTiposExistencia(te.data);
      setCuentasContables(cc.data);
      setModelos(mod.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar datos.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Helpers para buscar ID por codigo en las tablas relacionadas
  const findId = (list, idField, codigo) => {
    if (!codigo) return null;
    const cod = String(codigo).trim().toLowerCase();
    const found = list.find(item => String(item.codigo || '').trim().toLowerCase() === cod);
    return found ? found[idField] : null;
  };

  const getVal = (row, ...keys) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k];
    }
    return null;
  };

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const descripcion = getVal(row, 'Descripcion', 'descripcion', 'DESCRIPCION');
        let errorMsg = null;
        if (!descripcion) {
          fail++;
          errorMsg = `Fila ${i + 1}: Descripcion vacia (obligatoria)`;
        } else {
          // Validar lookups — si el codigo existe en el Excel pero no se encuentra en la BD, rechazar la fila
          const errors = [];
          const unidadCod = getVal(row, 'Unidad', 'unidad');
          const marcaCod = getVal(row, 'Marca', 'marca');
          const familiaCod = getVal(row, 'Familia', 'familia');
          const grupoCod = getVal(row, 'Grupo', 'grupo');
          const lineaCod = getVal(row, 'Linea', 'linea');
          const categoriaCod = getVal(row, 'Categoria', 'categoria');
          const tipoExistCod = getVal(row, 'Tipo Existencia', 'tipo_existencia');
          const cuentaCod = getVal(row, 'Cuenta Contable', 'cuenta_contable');
          const modeloCod = getVal(row, 'Modelo', 'modelo');

          if (unidadCod && !findId(unidades, 'id_unidad', unidadCod)) errors.push(`Unidad "${unidadCod}" no existe`);
          if (marcaCod && !findId(marcas, 'id_marca', marcaCod)) errors.push(`Marca "${marcaCod}" no existe`);
          if (familiaCod && !findId(familias, 'id_familia', familiaCod)) errors.push(`Familia "${familiaCod}" no existe`);
          if (grupoCod && !findId(grupos, 'id_grupo', grupoCod)) errors.push(`Grupo "${grupoCod}" no existe`);
          if (lineaCod && !findId(lineas, 'id_linea', lineaCod)) errors.push(`Linea "${lineaCod}" no existe`);
          if (categoriaCod && !findId(categorias, 'id_categoria', categoriaCod)) errors.push(`Categoria "${categoriaCod}" no existe`);
          if (tipoExistCod && !findId(tiposExistencia, 'id_tipo_existencia', tipoExistCod)) errors.push(`Tipo Existencia "${tipoExistCod}" no existe`);
          if (cuentaCod && !findId(cuentasContables, 'id_cuenta_contable', cuentaCod)) errors.push(`Cuenta Contable "${cuentaCod}" no existe`);
          if (modeloCod && !findId(modelos, 'id_modelo', modeloCod)) errors.push(`Modelo "${modeloCod}" no existe`);

          if (errors.length > 0) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${descripcion}"): ${errors.join('; ')}`;
          } else {
            try {
              const toStr = (v) => v != null ? String(v) : null;
              const data = {
                codigo_interno: toStr(getVal(row, 'Codigo', 'codigo_interno', 'codigo')),
                codigo_sunat: toStr(getVal(row, 'Codigo SUNAT', 'codigo_sunat')),
                descripcion: String(descripcion),
                descripcion_alt: toStr(getVal(row, 'Descripcion Alt', 'descripcion_alt')),
                id_unidad: findId(unidades, 'id_unidad', unidadCod),
                id_marca: findId(marcas, 'id_marca', marcaCod),
                id_modelo: findId(modelos, 'id_modelo', modeloCod),
                id_familia: findId(familias, 'id_familia', familiaCod),
                id_grupo: findId(grupos, 'id_grupo', grupoCod),
                id_linea: findId(lineas, 'id_linea', lineaCod),
                id_categoria: findId(categorias, 'id_categoria', categoriaCod),
                id_tipo_existencia: findId(tiposExistencia, 'id_tipo_existencia', tipoExistCod),
                id_cuenta_contable: findId(cuentasContables, 'id_cuenta_contable', cuentaCod),
                pais: toStr(getVal(row, 'Pais', 'pais')),
                peso: parseFloat(getVal(row, 'Peso', 'peso')) || null,
                ancho: parseFloat(getVal(row, 'Ancho', 'ancho')) || null,
                largo: parseFloat(getVal(row, 'Largo', 'largo')) || null,
                grosor: parseFloat(getVal(row, 'Grosor', 'grosor')) || null,
                volumen: parseFloat(getVal(row, 'Volumen', 'volumen')) || null,
                area: parseFloat(getVal(row, 'Area', 'area')) || null,
                medida: toStr(getVal(row, 'Medida', 'medida')),
                stock_minimo: parseFloat(getVal(row, 'Stock Minimo', 'stock_minimo')) || null,
                stock_maximo: parseFloat(getVal(row, 'Stock Maximo', 'stock_maximo')) || null,
              };
              await articuloService.create(data);
              ok++;
            } catch (err) {
              fail++;
              errorMsg = `Fila ${i + 1} ("${descripcion}"): ${getErrorDetail(err, 'Error al crear articulo')}`;
            }
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchAll();
    };
    return () => { window._dtImportHandler = null; };
  }, [unidades, marcas, familias, grupos, lineas, categorias]);

  const refreshCombo = async (type) => {
    try {
      if (type === 'unidades') { const r = await unidadMedidaService.getAll(); setUnidades(r.data); }
      else if (type === 'marcas') { const r = await tablaService('marcas').getAll(); setMarcas(r.data); }
      else if (type === 'familias') { const r = await tablaService('familias').getAll(); setFamilias(r.data); }
      else if (type === 'grupos') { const r = await tablaService('grupos').getAll(); setGrupos(r.data); }
      else if (type === 'lineas') { const r = await tablaService('lineas').getAll(); setLineas(r.data); }
      else if (type === 'categorias') { const r = await tablaService('categorias').getAll(); setCategorias(r.data); }
      else if (type === 'tipos-existencia') { const r = await tablaService('tipos-existencia').getAll(); setTiposExistencia(r.data); }
      else if (type === 'cuentas-contables') { const r = await tablaService('cuentas-contables').getAll(); setCuentasContables(r.data); }
      else if (type === 'modelos') { const r = await tablaService('modelos').getAll(); setModelos(r.data); }
    } catch {}
  };

  const openCreate = async () => {
    setForm(emptyForm);
    setEditing(null);
    setActiveTab('general');
    setAutoCode(true);
    try {
      const res = await articuloService.getNextCodigo();
      setForm(prev => ({ ...prev, codigo_interno: res.data.codigo }));
    } catch {}
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setActiveTab('general');
    setAutoCode(false);
    setForm({
      codigo_interno: r.codigo_interno || '', codigo_sunat: r.codigo_sunat || '',
      descripcion: r.descripcion || '', descripcion_alt: r.descripcion_alt || '',
      id_unidad: r.id_unidad || '', peso: r.peso || '', id_marca: r.id_marca || '',
      id_modelo: r.id_modelo || '', id_familia: r.id_familia || '', id_grupo: r.id_grupo || '',
      id_linea: r.id_linea || '', id_categoria: r.id_categoria || '',
      pais: r.pais || '', id_cuenta_contable: r.id_cuenta_contable || '', id_tipo_existencia: r.id_tipo_existencia || '',
      ancho: r.ancho || '', largo: r.largo || '', grosor: r.grosor || '',
      volumen: r.volumen || '', area: r.area || '', medida: r.medida || '',
      stock_minimo: r.stock_minimo || '', stock_maximo: r.stock_maximo || '',
      porcentaje_igv_compra: r.porcentaje_igv_compra != null ? r.porcentaje_igv_compra : 18,
    });
    setShowModal(true);
  };

  const toggleAutoCode = async () => {
    if (!autoCode) {
      try {
        const res = await articuloService.getNextCodigo();
        setForm(prev => ({ ...prev, codigo_interno: res.data.codigo }));
      } catch {}
    } else {
      setForm(prev => ({ ...prev, codigo_interno: '' }));
    }
    setAutoCode(!autoCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const generalRequired = ['codigo_interno', 'descripcion', 'id_tipo_existencia', 'id_cuenta_contable'];
    const clasifRequired = ['id_unidad', 'id_marca', 'id_modelo', 'id_familia', 'id_grupo', 'id_linea', 'id_categoria'];
    const missingGeneral = generalRequired.some(k => !form[k]);
    const missingClasif = clasifRequired.some(k => !form[k]);

    if (missingGeneral || missingClasif) {
      setActiveTab(missingGeneral ? 'general' : 'clasificacion');
      setAlert({ type: 'warning', message: 'Complete los campos obligatorios marcados en rojo.' });
      return;
    }

    const data = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v === '' || v === null) { data[k] = null; }
      else if (['id_unidad','id_marca','id_modelo','id_familia','id_grupo','id_linea','id_categoria','id_tipo_existencia','id_cuenta_contable'].includes(k)) { data[k] = parseInt(v) || null; }
      else if (['peso','ancho','largo','grosor','volumen','area','stock_minimo','stock_maximo','porcentaje_igv_compra'].includes(k)) { data[k] = parseFloat(v) || null; }
      else { data[k] = v; }
    });

    try {
      if (editing) {
        await articuloService.update(editing.id_articulo, data);
      } else {
        await articuloService.create(data);
      }
      setShowModal(false); fetchAll();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al guardar articulo.' });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Articulo', message: `¿Eliminar "${r.descripcion}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await articuloService.delete(r.id_articulo); setToast('Artículo eliminado'); fetchAll(); }
        catch (err) { setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar el artículo.') }); }
      },
    });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const setFormField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const openMiniModal = (type, label) => {
    setMiniModal({ type, label });
    setMiniForm({ codigo: '', descripcion: '', codigo_sunat: '' });
  };

  const handleMiniSubmit = async (e) => {
    e.preventDefault();
    if (!miniForm.codigo || !miniForm.descripcion) { setAlert({ type: 'warning', message: 'Codigo y descripcion son obligatorios.' }); return; }
    try {
      let res;
      if (miniModal.type === 'unidades') {
        res = await unidadMedidaService.create({ codigo: miniForm.codigo, codigo_sunat: miniForm.codigo_sunat || '', descripcion: miniForm.descripcion });
        await refreshCombo('unidades');
        if (res.data?.id_unidad) setForm(prev => ({ ...prev, id_unidad: res.data.id_unidad }));
      } else {
        const svc = tablaService(miniModal.type);
        res = await svc.create({ codigo: miniForm.codigo, descripcion: miniForm.descripcion });
        await refreshCombo(miniModal.type);
        const idMap = { marcas: 'id_marca', familias: 'id_familia', grupos: 'id_grupo', lineas: 'id_linea', categorias: 'id_categoria', 'tipos-existencia': 'id_tipo_existencia', 'cuentas-contables': 'id_cuenta_contable', modelos: 'id_modelo' };
        const formField = idMap[miniModal.type];
        if (formField && res.data[formField]) setForm(prev => ({ ...prev, [formField]: res.data[formField] }));
      }
      setMiniModal(null);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al crear.' });
    }
  };

  const tableColumns = [
    { key: 'codigo_interno', label: 'Codigo', width: 120, style: { fontWeight: 600 } },
    { key: 'descripcion', label: 'Descripcion' },
    { key: 'unidad_descripcion', label: 'Unidad', width: 100 },
    { key: 'marca_descripcion', label: 'Marca', width: 100 },
    { key: 'categoria_descripcion', label: 'Categoria', width: 100 },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Codigo', required: false, example: '0000000001', description: 'Codigo interno. Si se deja vacio, se genera automaticamente' },
    { key: 'Codigo SUNAT', label: 'Codigo SUNAT', required: false, example: '', description: 'Codigo SUNAT del articulo' },
    { key: 'Descripcion', label: 'Descripcion', required: true, example: 'Tornillo hexagonal 1/4"', description: 'Descripcion principal del articulo' },
    { key: 'Descripcion Alt', label: 'Descripcion Alt', required: false, example: '', description: 'Descripcion alternativa' },
    { key: 'Unidad', label: 'Unidad', required: false, example: 'UND', description: 'Codigo de unidad de medida (debe existir en Unidades de Medida)' },
    { key: 'Marca', label: 'Marca', required: false, example: '001', description: 'Codigo de marca (debe existir en Marcas)' },
    { key: 'Modelo', label: 'Modelo', required: false, example: '', description: 'Modelo del articulo' },
    { key: 'Familia', label: 'Familia', required: false, example: '001', description: 'Codigo de familia (debe existir en Familias)' },
    { key: 'Grupo', label: 'Grupo', required: false, example: '001', description: 'Codigo de grupo (debe existir en Grupos)' },
    { key: 'Linea', label: 'Linea', required: false, example: '001', description: 'Codigo de linea (debe existir en Lineas)' },
    { key: 'Categoria', label: 'Categoria', required: false, example: '001', description: 'Codigo de categoria (debe existir en Categorias)' },
    { key: 'Tipo Existencia', label: 'Tipo Existencia', required: false, example: 'Mercaderia', description: 'Tipo de existencia para SUNAT' },
    { key: 'Cuenta Contable', label: 'Cuenta Contable', required: false, example: '201101', description: 'Cuenta contable asociada' },
    { key: 'Pais', label: 'Pais', required: false, example: 'Peru', description: 'Pais de origen' },
    { key: 'Peso', label: 'Peso', required: false, example: '0.50', description: 'Peso en kg (decimal)' },
    { key: 'Ancho', label: 'Ancho', required: false, example: '', description: 'Ancho (decimal)' },
    { key: 'Largo', label: 'Largo', required: false, example: '', description: 'Largo (decimal)' },
    { key: 'Grosor', label: 'Grosor', required: false, example: '', description: 'Grosor (decimal)' },
    { key: 'Volumen', label: 'Volumen', required: false, example: '', description: 'Volumen (decimal)' },
    { key: 'Area', label: 'Area', required: false, example: '', description: 'Area (decimal)' },
    { key: 'Medida', label: 'Medida', required: false, example: 'cm', description: 'Unidad de las dimensiones (cm, mm, m, etc.)' },
    { key: 'Stock Minimo', label: 'Stock Minimo', required: false, example: '10', description: 'Stock minimo de alerta (decimal)' },
    { key: 'Stock Maximo', label: 'Stock Maximo', required: false, example: '1000', description: 'Stock maximo permitido (decimal)' },
  ];

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'clasificacion', label: 'Clasificacion' },
    { key: 'dimensiones', label: 'Dimensiones' },
    { key: 'stock', label: 'Stock' },
  ];

  return (
    <Layout title="Articulos" subtitle="Tablas Generales">
      <div className="page-header">
        <h2>Articulos</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo Articulo</button>
      </div>

      <DataTable
        columns={tableColumns}
        data={records}
        idField="id_articulo"
        onEdit={openEdit}
        onDelete={handleDelete}
        loading={loading}
        emptyMessage="No hay articulos registrados"
        exportFileName="articulos"
        importColumns={importCols}
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{editing ? 'Editar Articulo' : 'Articulo'}</h2>
                {!editing && (
                  <span className="modal-header-badge">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
                  </span>
                )}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="art-layout">
                <div className="art-tabs-sidebar">
                  {tabs.map(t => (
                    <button key={t.key} type="button" className={`art-tab-btn ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="art-tab-content">
                  {activeTab === 'general' && (
                    <>
                      <div className="form-section-divider" style={{borderTop:'none',paddingTop:0}}>Identificacion</div>
                      <div className="auto-code-row">
                        <div className="form-row" style={{flex:1}}>
                          <label>Codigo Interno *</label>
                          <input name="codigo_interno" value={form.codigo_interno} onChange={handleChange} placeholder="Codigo interno"
                            readOnly={autoCode && !editing}
                            style={{ ...(autoCode && !editing ? {opacity:0.7, cursor:'not-allowed'} : {}), ...(!form.codigo_interno ? { boxShadow: '0 0 0 1.5px #ef4444, 0 0 0 4px rgba(239,68,68,0.10)', borderRadius: 8 } : {}) }} />
                        </div>
                        {!editing && (
                          <div className="toggle-switch" onClick={toggleAutoCode}>
                            <div className={`toggle-track ${autoCode ? 'on' : ''}`}><div className="toggle-thumb" /></div>
                            <span className="toggle-label">Auto</span>
                          </div>
                        )}
                        <div className="form-row" style={{flex:1}}>
                          <label>Codigo SUNAT</label>
                          <input name="codigo_sunat" value={form.codigo_sunat} onChange={handleChange} placeholder="Codigo SUNAT" />
                        </div>
                      </div>
                      <div className="form-row">
                        <label>Descripcion *</label>
                        <input name="descripcion" value={form.descripcion} onChange={handleChange} placeholder="Descripcion del articulo"
                          style={!form.descripcion ? { boxShadow: '0 0 0 1.5px #ef4444, 0 0 0 4px rgba(239,68,68,0.10)', borderRadius: 8 } : undefined} />
                      </div>
                      <div className="form-row">
                        <label>Descripcion Alternativa</label>
                        <input name="descripcion_alt" value={form.descripcion_alt} onChange={handleChange} placeholder="Descripcion alternativa" />
                      </div>
                      <div className="form-section-divider">Contabilidad</div>
                      <div className="form-grid-3">
                        <AutoComplete label="Tipo Existencia *" value={form.id_tipo_existencia} options={tiposExistencia} idField="id_tipo_existencia" displayField="descripcion"
                          error={!form.id_tipo_existencia}
                          onChange={(v) => setFormField('id_tipo_existencia', v)} onAdd={() => openMiniModal('tipos-existencia','Tipo de Existencia')} addLabel="Tipo de Existencia" placeholder="Buscar tipo de existencia..." />
                        <AutoComplete label="Cuenta Contable *" value={form.id_cuenta_contable} options={cuentasContables} idField="id_cuenta_contable" displayField="descripcion"
                          error={!form.id_cuenta_contable}
                          onChange={(v) => setFormField('id_cuenta_contable', v)} onAdd={() => openMiniModal('cuentas-contables','Cuenta Contable')} addLabel="Cuenta Contable" placeholder="Buscar cuenta contable..." />
                        <div className="form-row"><label>Pais de Origen</label><input name="pais" value={form.pais} onChange={handleChange} placeholder="Pais" /></div>
                      </div>
                      <div className="form-grid-3">
                        <div className="form-row"><label>% IGV Compra</label><input name="porcentaje_igv_compra" type="number" step="0.01" min="0" max="100" value={form.porcentaje_igv_compra} onChange={handleChange} placeholder="18" /></div>
                      </div>
                    </>
                  )}

                  {activeTab === 'clasificacion' && (
                    <>
                      <div className="form-section-divider" style={{borderTop:'none',paddingTop:0}}>Unidad y Modelo</div>
                      <div className="form-grid-2">
                        <AutoComplete label="Unidad de Medida *" value={form.id_unidad} options={unidades} idField="id_unidad" displayField="descripcion"
                          error={!form.id_unidad}
                          onChange={(v) => setFormField('id_unidad', v)} onAdd={() => openMiniModal('unidades','Unidad')} addLabel="Unidad" placeholder="Buscar unidad..." />
                        <div className="form-row"><label>Peso</label><input name="peso" type="number" step="0.01" value={form.peso} onChange={handleChange} placeholder="0.00" /></div>
                      </div>
                      <div className="form-grid-2">
                        <AutoComplete label="Marca *" value={form.id_marca} options={marcas} idField="id_marca" displayField="descripcion"
                          error={!form.id_marca}
                          onChange={(v) => setFormField('id_marca', v)} onAdd={() => openMiniModal('marcas','Marca')} addLabel="Marca" placeholder="Buscar marca..." />
                        <AutoComplete label="Modelo *" value={form.id_modelo} options={modelos} idField="id_modelo" displayField="descripcion"
                          error={!form.id_modelo}
                          onChange={(v) => setFormField('id_modelo', v)} onAdd={() => openMiniModal('modelos','Modelo')} addLabel="Modelo" placeholder="Buscar modelo..." />
                      </div>
                      <div className="form-section-divider">Agrupacion</div>
                      <div className="form-grid-2">
                        <AutoComplete label="Familia *" value={form.id_familia} options={familias} idField="id_familia" displayField="descripcion"
                          error={!form.id_familia}
                          onChange={(v) => setFormField('id_familia', v)} onAdd={() => openMiniModal('familias','Familia')} addLabel="Familia" placeholder="Buscar familia..." />
                        <AutoComplete label="Grupo *" value={form.id_grupo} options={grupos} idField="id_grupo" displayField="descripcion"
                          error={!form.id_grupo}
                          onChange={(v) => setFormField('id_grupo', v)} onAdd={() => openMiniModal('grupos','Grupo')} addLabel="Grupo" placeholder="Buscar grupo..." />
                      </div>
                      <div className="form-grid-2">
                        <AutoComplete label="Linea *" value={form.id_linea} options={lineas} idField="id_linea" displayField="descripcion"
                          error={!form.id_linea}
                          onChange={(v) => setFormField('id_linea', v)} onAdd={() => openMiniModal('lineas','Linea')} addLabel="Linea" placeholder="Buscar linea..." />
                        <AutoComplete label="Categoria *" value={form.id_categoria} options={categorias} idField="id_categoria" displayField="descripcion"
                          error={!form.id_categoria}
                          onChange={(v) => setFormField('id_categoria', v)} onAdd={() => openMiniModal('categorias','Categoria')} addLabel="Categoria" placeholder="Buscar categoria..." />
                      </div>
                    </>
                  )}

                  {activeTab === 'dimensiones' && (
                    <>
                      <div className="form-section-divider" style={{borderTop:'none',paddingTop:0}}>Medidas</div>
                      <div className="form-grid-4">
                        <div className="form-row"><label>Ancho</label><input name="ancho" type="number" step="0.01" value={form.ancho} onChange={handleChange} placeholder="0.00" /></div>
                        <div className="form-row"><label>Largo</label><input name="largo" type="number" step="0.01" value={form.largo} onChange={handleChange} placeholder="0.00" /></div>
                        <div className="form-row"><label>Grosor</label><input name="grosor" type="number" step="0.01" value={form.grosor} onChange={handleChange} placeholder="0.00" /></div>
                        <div className="form-row"><label>Medida</label><input name="medida" value={form.medida} onChange={handleChange} placeholder="Unid." /></div>
                      </div>
                      <div className="form-grid-2">
                        <div className="form-row"><label>Volumen</label><input name="volumen" type="number" step="0.01" value={form.volumen} onChange={handleChange} placeholder="0.00" /></div>
                        <div className="form-row"><label>Area</label><input name="area" type="number" step="0.01" value={form.area} onChange={handleChange} placeholder="0.00" /></div>
                      </div>
                    </>
                  )}

                  {activeTab === 'stock' && (
                    <>
                      <div className="form-section-divider" style={{borderTop:'none',paddingTop:0}}>Control de Stock</div>
                      <div className="form-grid-2">
                        <div className="form-row"><label>Stock Minimo</label><input name="stock_minimo" type="number" step="0.01" value={form.stock_minimo} onChange={handleChange} placeholder="0.00" /></div>
                        <div className="form-row"><label>Stock Maximo</label><input name="stock_maximo" type="number" step="0.01" value={form.stock_maximo} onChange={handleChange} placeholder="0.00" /></div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar Cambios' : 'Crear Articulo'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {miniModal && (
        <div className="modal-overlay" style={{zIndex:300}}>
          <div className="modal" style={{maxWidth:360}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{miniModal.label}</h2>
                <span className="modal-header-badge">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
                </span>
              </div>
              <button className="modal-close" onClick={() => setMiniModal(null)}>&times;</button>
            </div>
            <form onSubmit={handleMiniSubmit}>
              <div className="modal-body">
                <div className="form-row"><label>Codigo *</label><input value={miniForm.codigo} onChange={e => setMiniForm({...miniForm, codigo: e.target.value})} placeholder="Codigo" autoFocus /></div>
                {miniModal.type === 'unidades' && (
                  <div className="form-row"><label>Codigo SUNAT</label><input value={miniForm.codigo_sunat} onChange={e => setMiniForm({...miniForm, codigo_sunat: e.target.value})} placeholder="Codigo SUNAT" /></div>
                )}
                <div className="form-row"><label>Descripcion *</label><input value={miniForm.descripcion} onChange={e => setMiniForm({...miniForm, descripcion: e.target.value})} placeholder="Descripcion" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setMiniModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default Articulos;
